import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState, ReactNode } from 'react';
import { STALE_TIME, GARBAGE_COLLECTION_TIME } from '@/config/constants';
import { globalQueryErrorHandler } from '@/lib/api-error-handler';

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * React Query Provider with optimized configuration for QPS application
 * Handles caching, background refetching, and error management
 */
export function QueryProvider({ children }: QueryProviderProps) {
  // Create QueryClient instance only once using useState
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Prevent refetch on window focus (mobile-friendly)
            refetchOnWindowFocus: false,

            // Data stays fresh for 24 hours (good for relatively stable data)
            staleTime: STALE_TIME,

            // Disable automatic retries for failed requests (handle manually)
            retry: false,

            // Cache garbage collection after 30 minutes of inactivity
            gcTime: GARBAGE_COLLECTION_TIME,

            // Don't refetch on reconnect unless data is stale
            refetchOnReconnect: 'always',

            // Network mode - go ahead with cached data if offline
            networkMode: 'online',
          },
          mutations: {
            // Disable retries for mutations (we handle errors manually)
            retry: false,

            // Network mode for mutations
            networkMode: 'online',
          },
        },

        // Global error handlers
        queryCache: new QueryCache({
          onError: (error: unknown, query: unknown) => {
            console.error('Query error:', error, 'Query:', query);
            globalQueryErrorHandler(error);
          },
        }),

        mutationCache: new MutationCache({
          onError: (error: unknown, variables: unknown) => {
            console.error('Mutation error:', error, 'Variables:', variables);
            globalQueryErrorHandler(error);
          },
        }),
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
